import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';
import PageMeta from '@/components/landing/PageMeta';
import { openCookiePreferences } from '@/components/CookieConsent';

const BG = '#06070A';

const H = {
  fontFamily: 'Inter, system-ui, sans-serif',
  color: '#F1F5F9',
  fontWeight: 500,
  letterSpacing: '-0.01em',
  marginTop: 32,
  marginBottom: 12,
};

const P = {
  fontFamily: 'Inter, system-ui, sans-serif',
  color: '#94A3B8',
  fontSize: 15,
  lineHeight: 1.75,
  margin: '0 0 14px',
};

const KICKER = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 10,
  letterSpacing: '0.22em',
  color: '#475569',
  textTransform: 'uppercase',
};

export default function Privacy() {
  return (
    <div style={{ background: BG, minHeight: '100vh', color: '#F1F5F9' }}>
      <PageMeta
        title="Privacy Policy — APEX Verity"
        description="How APEX Verity handles cookies, analytics, and personal data. Apex products are ad-free. We do not sell or share your data."
        path="/privacy"
      />
      <LandingNav />

      <main style={{ maxWidth: 760, margin: '0 auto', padding: '140px 24px 80px' }}>
        <div style={KICKER}>Legal · Last updated 14 Feb 2026</div>
        <h1 style={{ ...H, fontSize: 'clamp(32px, 4vw, 44px)', marginTop: 14, marginBottom: 28 }}>
          Privacy Policy
        </h1>

        <p style={P}>
          This Privacy Policy describes how APEX Verity ("Apex", "we", "our") handles information on
          our public marketing pages at <strong>apexverity.io</strong> and product surfaces. Apex products
          are ad-free; we do not sell, share, or rent your personal data.
        </p>

        <h2 style={{ ...H, fontSize: 22 }}>1. What we collect</h2>
        <p style={P}>
          <strong style={{ color: '#F1F5F9' }}>Essential.</strong> A small set of cookies and local-storage
          values required for the site to function — session state, security flags, and your cookie
          preferences themselves (key <code style={code()}>apex-cookie-consent</code>).
        </p>
        <p style={P}>
          <strong style={{ color: '#F1F5F9' }}>Analytics (optional).</strong> If you opt in, we load
          Google Analytics (gtag.js, measurement ID <code style={code()}>G-B71WPMJZ0D</code>) to count
          pageviews and understand which pages are useful. Google may set cookies prefixed{' '}
          <code style={code()}>_ga</code>. We do not enable Google Signals, Ads, or remarketing.
        </p>
        <p style={P}>
          <strong style={{ color: '#F1F5F9' }}>Preferences (optional).</strong> If you opt in, we
          remember UI choices (theme, layout, dismissed banners) in your browser's local storage.
          This data never leaves your device.
        </p>
        <p style={P}>
          <strong style={{ color: '#F1F5F9' }}>Forms.</strong> When you submit an access-request or
          contact form, the fields you provide are forwarded to our form-processing partner (Formspree)
          and emailed to the Apex team. Standard log information (IP address, user agent, timestamp)
          may be retained by Formspree per their own policy.
        </p>

        <h2 style={{ ...H, fontSize: 22 }}>2. Lawful basis</h2>
        <p style={P}>
          We rely on consent (GDPR Art. 6(1)(a)) for analytics and preference cookies, and on
          legitimate interest (Art. 6(1)(f)) for essential cookies, security, and form processing
          you initiate.
        </p>

        <h2 style={{ ...H, fontSize: 22 }}>3. Cookie categories</h2>
        <ul style={{ ...P, paddingLeft: 22, margin: '0 0 14px' }}>
          <li style={{ marginBottom: 8 }}>
            <strong style={{ color: '#F1F5F9' }}>Essential</strong> — always on, cannot be disabled.
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong style={{ color: '#F1F5F9' }}>Analytics</strong> — optional, default OFF, opt-in only.
          </li>
          <li style={{ marginBottom: 8 }}>
            <strong style={{ color: '#F1F5F9' }}>Preferences</strong> — optional, default OFF, opt-in only.
          </li>
        </ul>

        <h2 style={{ ...H, fontSize: 22 }}>4. Withdrawing consent</h2>
        <p style={P}>
          You can change or withdraw your consent at any time by clicking{' '}
          <button
            type="button"
            onClick={openCookiePreferences}
            data-testid="privacy-open-prefs"
            style={inlineBtn()}
          >
            Cookie Preferences
          </button>{' '}
          here or in the site footer. Your decision is stored for 12 months and then you will be
          prompted again, in line with EDPB guidance.
        </p>

        <h2 style={{ ...H, fontSize: 22 }}>5. Your rights</h2>
        <p style={P}>
          Under GDPR / UK GDPR, you have the right to access, rectify, erase, and port your data, to
          restrict or object to processing, and to lodge a complaint with your supervisory authority.
          Email <a href="mailto:privacy@apexverity.io" style={mailLink()}>privacy@apexverity.io</a> to
          exercise any of these.
        </p>

        <h2 style={{ ...H, fontSize: 22 }}>6. Contact</h2>
        <p style={P}>
          Apex Verity · <a href="mailto:hello@apexverity.io" style={mailLink()}>hello@apexverity.io</a>{' '}
          · <Link to="/contact" style={mailLink()}>Contact form</Link>
        </p>

        <div style={{ ...KICKER, marginTop: 56 }}>End of policy</div>
      </main>

      <LandingFooter />
    </div>
  );
}

function code() {
  return {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: 12,
    background: '#10131C',
    border: '1px solid #14171F',
    padding: '1px 6px',
    borderRadius: 2,
    color: '#E2E8F0',
  };
}

function mailLink() {
  return { color: '#F1F5F9', textDecoration: 'underline', textUnderlineOffset: 3 };
}

function inlineBtn() {
  return {
    background: 'transparent',
    color: '#F1F5F9',
    border: 'none',
    padding: 0,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'inherit',
  };
}
